import axios from 'axios';

// Create an axios instance with default config
const api = axios.create({
  baseURL: 'https://forkastres-api.hasansib9.workers.dev/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add an interceptor for authentication
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auth services
export const authService = {
  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      if (response.data.token) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  },
  
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem('authToken');
  },
};

// Restaurant services
export const restaurantService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/restaurants', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getById: async (id) => {
    try {
      const response = await api.get(`/restaurants/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  create: async (restaurantData) => {
    try {
      const response = await api.post('/restaurants', restaurantData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  update: async (id, restaurantData) => {
    try {
      const response = await api.put(`/restaurants/${id}`, restaurantData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  delete: async (id) => {
    try {
      const response = await api.delete(`/restaurants/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Floor plan specific endpoints
  getFloorPlan: async (restaurantId) => {
    try {
      const response = await api.get(`/restaurants/${restaurantId}/floor-plan`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  updateFloorPlan: async (restaurantId, floorPlanData) => {
    try {
      const response = await api.put(`/restaurants/${restaurantId}/floor-plan`, floorPlanData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Menu specific endpoints
  getMenu: async (restaurantId) => {
    try {
      const response = await api.get(`/restaurants/${restaurantId}/menu`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  updateMenu: async (restaurantId, menuData) => {
    try {
      const response = await api.put(`/restaurants/${restaurantId}/menu`, menuData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// User management services
export const userService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/users', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getById: async (id) => {
    try {
      const response = await api.get(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  create: async (userData) => {
    try {
      const response = await api.post('/users', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  update: async (id, userData) => {
    try {
      const response = await api.put(`/users/${id}`, userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  delete: async (id) => {
    try {
      const response = await api.delete(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Reservation services
export const reservationService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/reservations', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getById: async (id) => {
    try {
      const response = await api.get(`/reservations/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  create: async (reservationData) => {
    try {
      const response = await api.post('/reservations', reservationData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  update: async (id, reservationData) => {
    try {
      const response = await api.put(`/reservations/${id}`, reservationData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  delete: async (id) => {
    try {
      const response = await api.delete(`/reservations/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Analytics and reporting services
export const reportService = {
  getRevenueReport: async (params = {}) => {
    try {
      const response = await api.get('/reports/revenue', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getReservationsReport: async (params = {}) => {
    try {
      const response = await api.get('/reports/reservations', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getCustomerReport: async (params = {}) => {
    try {
      const response = await api.get('/reports/customers', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getDashboardStats: async () => {
    try {
      const response = await api.get('/reports/dashboard');
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

export default api; 